import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';
import { getGlassClasses } from '../../../utils/theme';

/**
 * GlassBadge Component
 *
 * A glassmorphic badge component for status indicators, labels, and tags.
 */

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full font-medium transition-all duration-200',
  {
    variants: {
      variant: {
        glass: cn(getGlassClasses('subtle'), 'text-white/90'),
        'glass-primary': cn(
          getGlassClasses('default'),
          'bg-primary-500/20 text-primary-100 border-primary-400/30'
        ),
        'glass-secondary': cn(
          getGlassClasses('default'),
          'bg-secondary-500/20 text-secondary-100 border-secondary-400/30'
        ),
        'glass-accent': cn(
          getGlassClasses('default'),
          'bg-accent-500/20 text-accent-950 border-accent-400/30'
        ),
        'glass-success': cn(
          getGlassClasses('default'),
          'bg-success-500/20 text-success-100 border-success-400/30'
        ),
        'glass-warning': cn(
          getGlassClasses('default'),
          'bg-warning-500/20 text-warning-100 border-warning-400/30'
        ),
        'glass-error': cn(
          getGlassClasses('default'),
          'bg-error-500/20 text-error-100 border-error-400/30'
        ),
        info: cn(
          getGlassClasses('default'),
          'bg-blue-500/20 text-blue-100 border-blue-400/30'
        ),
        'glass-info': cn(
          getGlassClasses('default'),
          'bg-blue-500/20 text-blue-100 border-blue-400/30'
        ),
        primary: 'bg-primary-500 text-white',
        secondary: 'bg-secondary-500 text-white',
        success: 'bg-success-500 text-white',
        warning: 'bg-warning-500 text-warning-950',
        error: 'bg-error-500 text-white',
        destructive: 'bg-red-500 text-white',
        default: 'bg-gray-500/20 text-gray-100 border-gray-400/30',
        outline: 'border-2 border-primary-500 text-primary-400 bg-transparent',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
        lg: 'px-3 py-1.5 text-base',
      },
      interactive: {
        true: 'cursor-pointer hover:scale-105 active:scale-95',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'glass',
      size: 'md',
      interactive: false,
    },
  }
);

export interface GlassBadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Badge content */
  children: React.ReactNode;
  /** Icon to display before text */
  icon?: React.ReactNode;
  /** Icon to display after text (e.g., close button) */
  rightIcon?: React.ReactNode;
  /** Click handler (makes badge interactive) */
  onRemove?: () => void;
}

export const GlassBadge = React.forwardRef<HTMLSpanElement, GlassBadgeProps>(
  (
    {
      className,
      variant,
      size,
      interactive,
      children,
      icon,
      rightIcon,
      onRemove,
      onClick,
      ...props
    },
    ref
  ) => {
    const isInteractive = interactive || !!onClick || !!onRemove;

    return (
      <span
        ref={ref}
        className={cn(
          badgeVariants({
            variant,
            size,
            interactive: isInteractive,
            className,
          })
        )}
        onClick={onClick}
        {...props}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span>{children}</span>
        {(rightIcon || onRemove) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            className="flex-shrink-0 hover:opacity-70 transition-opacity"
          >
            {rightIcon || (
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
          </button>
        )}
      </span>
    );
  }
);

GlassBadge.displayName = 'GlassBadge';

/**
 * Status Badge - Preset for common statuses
 */
export interface StatusBadgeProps extends Omit<GlassBadgeProps, 'variant' | 'children'> {
  status: 'active' | 'inactive' | 'pending' | 'error' | 'success' | 'warning';
  children?: React.ReactNode;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  children,
  ...props
}) => {
  const variantMap = {
    active: 'glass-success' as const,
    inactive: 'glass-secondary' as const,
    pending: 'glass-warning' as const,
    error: 'glass-error' as const,
    success: 'glass-success' as const,
    warning: 'glass-warning' as const,
  };

  const iconMap = {
    active: (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
    inactive: (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"
          clipRule="evenodd"
        />
      </svg>
    ),
    pending: (
      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    ),
    error: (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    ),
    success: (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
    warning: (
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
    ),
  };

  return (
    <GlassBadge variant={variantMap[status]} icon={iconMap[status]} {...props}>
      {children || status.charAt(0).toUpperCase() + status.slice(1)}
    </GlassBadge>
  );
};

StatusBadge.displayName = 'StatusBadge';
